import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { NgxGalleryModule, NgxGalleryOptions, NgxGalleryImage, NgxGalleryAnimation } from '@kolkov/ngx-gallery';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { validateOrdersByAction } from 'app/utils/document-class-validation';
import { DocumentClassAttributes, OrderAttributes } from '@albatrosdeveloper/ave-models-package';
import { OrdersListService } from 'app/modules/components/sales/order-list-components/order-list.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DocumentTypeEnum } from 'app/common/enums/document-types.enum';

@Component({
  selector: "app-modal-image",
  templateUrl: "./modal-image.component.html",
  styleUrls: ["./modal-image.scss"],
  imports: [
    MatDialogModule,
    MatTabsModule,
    CommonModule,
    MatIconModule,
    MatButtonModule,
    NgxGalleryModule,
  ],
  standalone: true,
})
export class ImageCarouselComponent {
  galleryOptions: NgxGalleryOptions[];
  galleryImages: NgxGalleryImage[];
  images: NgxGalleryImage[];

  selectedFiles: File[] = [];
  uploadProgress = 0;
  isUploading = false;

  isDragging = false;
  dragCounter = 0;

  validTypes = [
    DocumentTypeEnum.SOLICITUD_CAMBIO_PRODUCTO,
    DocumentTypeEnum.CAMBIO_DE_PRODUCTO,
    DocumentTypeEnum.SOLICITUD_DEVOLUCION_PRODUCTO,
  ];
  isTypeValid: boolean = false;
  canEdit: boolean = false;

  documentClassesList: DocumentClassAttributes[] = [];

  constructor(
    private readonly ordersListService: OrdersListService,
    private readonly snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ImageCarouselComponent>,
  ) {
    this.images = data.images || [];
  }

  ngOnInit() {
    this.fetchDocumentClass();
    this.isTypeValid = this.isValidType(this.data.type);
    this.galleryOptions = [
      {
        width: "600px",
        height: "400px",
        thumbnailsColumns: 4,
        imageAnimation: NgxGalleryAnimation.Slide,
      },
      // max-width 800
      {
        breakpoint: 800,
        width: "80%",
        height: "600px",
        imagePercent: 80,
        thumbnailsPercent: 20,
        thumbnailsMargin: 20,
        thumbnailMargin: 20,
      },
      // max-width 400
      {
        breakpoint: 400,
        preview: false,
      },
    ];
    // Configuración para ngx-gallery
    this.galleryImages = this.images.map((image) => ({
      small: image, // URL de la imagen principal
      medium: image, // Miniatura (también se puede usar la misma URL)
      big: image, // Título opcional
    }));
  }

  private fetchDocumentClass(): void {
    let dataToFilter = {
      type: this.data.type,
    };
    this.ordersListService
      .fetchDocumentClasses(dataToFilter)
      .subscribe((res) => {
        this.documentClassesList = res.data;
        this.canEdit = this.validateDataToEdit(this.data.order);
      });
  }

  isValidType(type: string): boolean {
    return this.validTypes.includes(type as DocumentTypeEnum);
  }

  validateDataToEdit(order: OrderAttributes): boolean {
    return validateOrdersByAction([order], this.documentClassesList, "EDIT");
  }

  onFileSelected(event: any) {
    this.selectedFiles = Array.from(event.target.files);
  }

  async uploadImages() {
    if (this.selectedFiles.length === 0) return;

    this.isUploading = true;
    this.uploadProgress = 0;

    try {
      const base64Images = await this.convertFilesToBase64(this.selectedFiles);

      const payload = {
        detail: [
          {
            position: this.data.position,
            evidencesChange: [
              //  Existentes
              ...this.data.images.map((url) => ({
                type: "P",
                format: "jpeg",
                image: url.trim(),
              })),
              //  Nuevas — base64 
              ...base64Images.map((image) => ({
                type: "P",
                format: this.getImageFormat(image),
                image: this.extractBase64Data(image),
              })),
            ],
          },
        ],
      };

      await this.updateOrder(payload);

      base64Images.forEach((image) => {
        this.galleryImages.push({
          small: image,
          medium: image,
          big: image,
        });
      });

      this.uploadProgress = 100;
      this.galleryImages = [...this.galleryImages];
      this.selectedFiles = [];
    } catch (error) {
      this.snackBar.open(
        error?.error?.message || "Error al subir imágenes.",
        "Cerrar",
        { duration: 5000 },
      );
    } finally {
      this.isUploading = false;
    }
  }

  updateOrder(dataSource) {
    if (!dataSource) {
      return;
    }
    this.ordersListService
      .updateV2(String(this.data.order._id), dataSource)
      .subscribe({
        next: () => {
          this.snackBar.open("Imagen subida con éxito", "Cerrar", {
            duration: 3000,
          });
          this.dialogRef.close("Success");
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error al subir la imagen",
            "Cerrar",
            { duration: 3000 },
          );
        },
      });
  }

  // función para convertir URL a Base64
  private async urlToBase64(url: string): Promise<string> {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      this.snackBar.open(
        error.error.message || "Error convirtiendo URL a Base64",
        "Cerrar",
        { duration: 5000 },
      );
      throw error;
    }
  }

  private async convertFilesToBase64(files: File[]): Promise<string[]> {
    const conversions = files.map((file) => this.fileToBase64(file));
    return Promise.all(conversions);
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  private getImageFormat(base64String: string): string {
    const matches = base64String.match(/^data:image\/([a-zA-Z+]+);base64,/);
    return matches ? matches[1] : "jpeg";
  }

  private extractBase64Data(base64String: string): string {
    return base64String.split(",")[1];
  }

  // Simulación de envío (eliminar en producción)
  private simulateUpload(payload: any): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 1500);
    });
  }

  removeImage(index: number) {
    this.galleryImages.splice(index, 1);
    this.galleryImages = [...this.galleryImages];
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragEnter(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter++;
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter--;
    if (this.dragCounter === 0) {
      this.isDragging = false;
    }
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    this.dragCounter = 0;

    if (event.dataTransfer?.files) {
      this.selectedFiles = Array.from(event.dataTransfer.files);
      this.validateAndFilterImages();
    }
  }

  private validateAndFilterImages() {
    this.selectedFiles = this.selectedFiles.filter(
      (file) => file.type.match(/image\/*/) && file.size <= 5 * 1024 * 1024, // 5MB max
    );
  }

  getPreview(file: File): string {
    return URL.createObjectURL(file);
  }

  removeSelected(index: number) {
    this.selectedFiles.splice(index, 1);
  }

  closeModal() {
    this.dialogRef.close();
  }
}
